import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cssMap, cx } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { useDebouncedCallback } from 'use-debounce';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import { IconButton } from '@atlaskit/button/new';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { CardVariant } from '../../../common/ui/knowledge-smart-card';
import type {
	CarouselElement,
	ParsedElement,
	PersonElement,
	ProjectHighlightElement,
	SmartLinkElement,
} from '../../dynamic-layout-parser/types/dynamic-layout-section';

import type { ComponentPosition } from './dynamic-layout-component';
import { DynamicLayoutPersonCard } from './dynamic-layout-person-card';
import { DynamicLayoutSmartLink } from './dynamic-layout-smart-link';
import { DynamicLayoutProjectHighlightCard } from './sub-agents/dynamic-layout-project-highlight-card';

// TODO: Fix wonky scroll behaviour on carousel
const carouselCardWidth = 355; // card width (300px) + gap (space.200 = 16px)
const scrollWidth = (carouselCardWidth + 46) * 2;

const styles = cssMap({
	carouselStyles: {
		display: 'flex',
		alignItems: 'flex-start',
		position: 'relative',
		flexDirection: 'column',
		width: '100%',
		gap: token('space.050'),
	},
	carouselStylesSingleRow: {
		display: 'flex',
		alignItems: 'flex-start',
		position: 'relative',
		flexDirection: 'column',
		width: '100%',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: token('space.negative.200'),
	},
	scrollContainerStyles: {
		overflowX: 'auto',
		overflowY: 'hidden',
		display: 'block',
		width: '100%',
		scrollbarWidth: 'none',
		scrollBehavior: 'smooth',
	},
	cardContainerStyles: {
		display: 'flex',
		gap: token('space.200'),
		whiteSpace: 'nowrap',
	},
	carouselItemStyles: {
		flexGrow: 1,
		flexShrink: 1,
		flexBasis: '0%',
		minWidth: '220px',
		maxWidth: '360px',
	},
});

export type DocumentsCarouselProps = {
	component: CarouselElement;
	position: ComponentPosition;
	showArrows?: boolean;
};

type CarouselItemRendererProps = {
	child: ParsedElement;
	index: number;
};

const CarouselItemRenderer = ({ child }: CarouselItemRendererProps): React.JSX.Element => {
	const isPeopleCard = typeof child !== 'string' && child.tag === 'people-card';
	const isSmartLink = typeof child !== 'string' && child.tag === 'smart-link';
	const isProjectHighlight = typeof child !== 'string' && child.tag === 'project-highlight-card';

	if (isPeopleCard) {
		return (
			<Box testId="carousel-item-people-card" xcss={styles.carouselItemStyles}>
				<DynamicLayoutPersonCard component={child as PersonElement} />
			</Box>
		);
	}

	if (isSmartLink) {
		return (
			<Box testId="carousel-item-smart-link" xcss={styles.carouselItemStyles}>
				<DynamicLayoutSmartLink
					component={child as SmartLinkElement}
					variant={CardVariant.COMPACT}
				/>
			</Box>
		);
	}

	if (isProjectHighlight) {
		return (
			<Box testId="carousel-item-project-highlight-card" xcss={styles.carouselItemStyles}>
				<DynamicLayoutProjectHighlightCard component={child as ProjectHighlightElement} />
			</Box>
		);
	}

	return <Box />;
};

const isScrollAtLeft = (elem: HTMLDivElement | null) => elem?.scrollLeft === 0;
const isScrollAtRight = (elem: HTMLDivElement | null) =>
	elem ? Math.abs(elem?.scrollWidth - elem?.scrollLeft - elem?.clientWidth) <= 1 : false;

export const DynamicLayoutCarousel = ({
	component,
	position,
	showArrows = true,
}: DocumentsCarouselProps): React.JSX.Element | null => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [hideLeftScroll, setHideLeftScroll] = useState(true);
	const [hideRightScroll, setHideRightScroll] = useState(false);

	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	const key_id = useRef(uuidv4());

	const carouselChildren = (component.children || []) as ParsedElement[];
	const shouldAlignHeader = position === 'single-column';

	const updateArrowVisibility = useCallback(() => {
		if (scrollContainerRef.current) {
			setHideLeftScroll(isScrollAtLeft(scrollContainerRef.current));
			setHideRightScroll(isScrollAtRight(scrollContainerRef.current));
		}
	}, []);

	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;
		if (scrollContainer && carouselChildren.length) {
			updateArrowVisibility();

			const unbind = bind(scrollContainer, {
				type: 'scroll',
				listener: updateArrowVisibility,
			});

			return unbind;
		}
	}, [carouselChildren.length, updateArrowVisibility]);

	const onScroll = useDebouncedCallback(() => {
		updateArrowVisibility();
	}, 30);

	const scrollLeft = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollLeft -= scrollWidth;
		}
	};

	const scrollRight = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollLeft += scrollWidth;
		}
	};

	if (!carouselChildren || carouselChildren.length === 0) {
		return null;
	}

	return (
		<Box
			xcss={cx(shouldAlignHeader ? styles.carouselStylesSingleRow : styles.carouselStyles)}
			testId="dynamic-layout-carousel"
		>
			<Inline spread="space-between" grow="fill" alignBlock="center">
				{!shouldAlignHeader && component.attributes?.title ? (
					<Inline space="space.100" alignBlock="center">
						<Text
							weight="semibold"
							size="small"
							color="color.text.accent.gray"
							testId="carousel-title"
						>
							{component.attributes?.title}
						</Text>
					</Inline>
				) : (
					<Box />
				)}
				{showArrows && (!hideLeftScroll || !hideRightScroll) && (
					<Inline space="space.100">
						<IconButton
							icon={ArrowLeftIcon}
							label="scroll left"
							onClick={scrollLeft}
							shape="circle"
							appearance="subtle"
							spacing="compact"
							isDisabled={hideLeftScroll}
						/>
						<IconButton
							icon={ArrowRightIcon}
							label="scroll right"
							onClick={scrollRight}
							shape="circle"
							appearance="subtle"
							spacing="compact"
							isDisabled={hideRightScroll}
						/>
					</Inline>
				)}
			</Inline>
			<Box xcss={styles.scrollContainerStyles} ref={scrollContainerRef} onScroll={onScroll as any}>
				<Box xcss={styles.cardContainerStyles}>
					{carouselChildren.map((child, index) => (
						<CarouselItemRenderer key={`${key_id}-${index}`} child={child} index={index} />
					))}
				</Box>
			</Box>
		</Box>
	);
};
